import { useState, useEffect } from 'react'
import { useExchange } from '../hooks/useExchange'
import { getExchanges, deleteExchange } from '../api/services'
import type { ExchangeTransaction } from '../api/types'
import type { ReceiptTransactionData } from '@abcpay/shared'
import { printReceipt } from '../utils/printer'
import FlagIcon from './FlagIcon'
import ConfirmModal from './ConfirmModal'

const currencyMap: Record<string, string> = {
  USD: 'us',
  JPY: 'jp',
  EUR: 'eu',
  CNY: 'cn',
  HKD: 'hk',
  TWD: 'tw',
  PHP: 'ph',
  VND: 'vn',
  THB: 'th',
  SGD: 'sg',
  AUD: 'au',
  CAD: 'ca',
  GBP: 'gb',
  KRW: 'kr',
}

export default function HistoryTab() {
  const { selectedBranch } = useExchange()
  const [exchanges, setExchanges] = useState<ExchangeTransaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [exchangeToDelete, setExchangeToDelete] = useState<ExchangeTransaction | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedExchange, setSelectedExchange] = useState<ExchangeTransaction | null>(null)
  const [isPrinting, setIsPrinting] = useState(false)

  const fetchHistory = async () => {
    if (!selectedBranch) return

    setLoading(true)
    setError(null)
    try {
      const response = await getExchanges({
        branchNid: selectedBranch.nid,
        page: 1,
        limit: 50,
        includeDeleted: false
      })
      setExchanges(response.exchanges)
    } catch (err: any) {
      console.error(err)
      setError('거래 기록을 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHistory()
  }, [selectedBranch])

  const checkIsCancellable = (createdAt: string) => {
    const createdTime = new Date(createdAt).getTime()
    const currentTime = new Date().getTime()
    const diffMinutes = (currentTime - createdTime) / (1000 * 60)
    return diffMinutes <= 30
  }

  const handleDeleteClick = (exchange: ExchangeTransaction) => {
    setExchangeToDelete(exchange)
    setDeleteModalOpen(true)
  }

  const exchangeToReceiptData = (ex: ExchangeTransaction): ReceiptTransactionData => {
    const isBuy = ex.rateType === 'bid'
    return {
      transactionNumber: ex.nid,
      selectedCurrency: isBuy ? ex.fromCurrency : ex.toCurrency,
      rate: ex.appliedRate,
      foreignAmount: isBuy ? ex.fromAmount : ex.toAmount,
      krwAmount: isBuy ? ex.toAmount : ex.fromAmount,
      tradeType: isBuy ? 'buy' : 'sell',
      timestamp: new Date(ex.createdAt),
    }
  }

  const handleRowClick = (ex: ExchangeTransaction) => {
    setSelectedExchange(ex)
    setDetailModalOpen(true)
  }

  const handlePrintReceipt = async () => {
    if (!selectedExchange || isPrinting) return
    setIsPrinting(true)
    try {
      const data = exchangeToReceiptData(selectedExchange)
      const result = await printReceipt(data)
      if (!result.success) {
        alert(result.message || '영수증 출력에 실패했습니다.')
      }
    } catch {
      alert('영수증 출력에 실패했습니다.')
    } finally {
      setIsPrinting(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedBranch || !exchangeToDelete) return

    setIsDeleting(true)
    try {
      await deleteExchange({
        branchNid: selectedBranch.nid,
        exchangeNid: exchangeToDelete.nid
      })
      
      // Close modal and refresh list
      setDeleteModalOpen(false)
      setExchangeToDelete(null)
      fetchHistory() // Refresh the list
    } catch (err) {
      console.error('Failed to delete exchange:', err)
      alert('거래 취소에 실패했습니다.')
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatNumber = (num: string | number) => {
    return Number(num).toLocaleString('ko-KR')
  }

  const getCountryCode = (currencyCode: string) => {
    return currencyMap[currencyCode] || 'unknown'
  }

  if (loading && exchanges.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-red-500">{error}</p>
        <button 
          onClick={fetchHistory} 
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          다시 시도
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">시간</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">유형</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">손님이 준 돈 (From)</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">손님이 받은 돈 (To)</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">적용 환율</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">작업</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {exchanges.map((ex) => {
              const isCancellable = checkIsCancellable(ex.createdAt)
              
              return (
                <tr key={ex.nid} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleRowClick(ex)}>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                    {formatDate(ex.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      ex.rateType === 'bid' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {ex.rateType === 'bid' ? '매입 (Buy)' : '매각 (Sell)'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                       <FlagIcon countryCode={getCountryCode(ex.fromCurrency)} size="sm" className="rounded-full shadow-sm" />
                       <span className="font-medium">{formatNumber(ex.fromAmount)}</span>
                       <span className="text-gray-500">{ex.fromCurrency}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                       <FlagIcon countryCode={getCountryCode(ex.toCurrency)} size="sm" className="rounded-full shadow-sm" />
                       <span className="font-medium">{formatNumber(ex.toAmount)}</span>
                       <span className="text-gray-500">{ex.toCurrency}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {formatNumber(ex.appliedRate)}
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); isCancellable && handleDeleteClick(ex) }}
                      disabled={!isCancellable}
                      className={`font-medium px-3 py-1 rounded transition-colors ${
                        isCancellable 
                          ? 'text-red-600 hover:text-red-800 hover:bg-red-50' 
                          : 'text-gray-400 cursor-not-allowed bg-gray-100'
                      }`}
                    >
                      취소
                    </button>
                  </td>
                </tr>
              )
            })}
            {exchanges.length === 0 && (
                <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        거래 기록이 없습니다.
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        title="거래 취소"
        message={exchangeToDelete ?
          `다음 거래를 취소하시겠습니까?\n\n${exchangeToDelete.rateType === 'bid' ? '[매입]' : '[매각]'}\n${formatDate(exchangeToDelete.createdAt)}\n${formatNumber(exchangeToDelete.fromAmount)} ${exchangeToDelete.fromCurrency}   ${formatNumber(exchangeToDelete.toAmount)} ${exchangeToDelete.toCurrency}`
          : "거래를 취소하시겠습니까?"}
        confirmText={isDeleting ? "취소 중..." : "거래 취소"}
        cancelText="닫기"
        onConfirm={handleConfirmDelete}
        onCancel={() => !isDeleting && setDeleteModalOpen(false)}
        variant="danger"
      />

      {/* Transaction Detail Modal */}
      <ConfirmModal
        isOpen={detailModalOpen}
        title="거래 상세"
        confirmText={isPrinting ? "출력 중..." : "영수증 출력"}
        cancelText="닫기"
        onConfirm={handlePrintReceipt}
        onCancel={() => !isPrinting && setDetailModalOpen(false)}
        variant="primary"
      >
        {selectedExchange && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">유형</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                selectedExchange.rateType === 'bid'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {selectedExchange.rateType === 'bid' ? '매입 (Buy)' : '매각 (Sell)'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FlagIcon countryCode={getCountryCode(selectedExchange.fromCurrency)} size="sm" className="rounded-full shadow-sm" />
                <span className="font-medium text-lg">{formatNumber(selectedExchange.fromAmount)}</span>
                <span className="text-gray-500">{selectedExchange.fromCurrency}</span>
              </div>
              <span className="text-gray-400 text-lg">&rarr;</span>
              <div className="flex items-center gap-2">
                <FlagIcon countryCode={getCountryCode(selectedExchange.toCurrency)} size="sm" className="rounded-full shadow-sm" />
                <span className="font-medium text-lg">{formatNumber(selectedExchange.toAmount)}</span>
                <span className="text-gray-500">{selectedExchange.toCurrency}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">적용 환율</span>
              <span className="font-medium">{formatNumber(selectedExchange.appliedRate)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">거래 시간</span>
              <span className="font-medium">{formatDate(selectedExchange.createdAt)}</span>
            </div>
          </div>
        )}
      </ConfirmModal>
    </div>
  )
}
